// Console API client: cookie session, JSON both ways. A 401 sends the user to the sign-in page.
import type { FontChoice } from '../../src/lib/theme-css'
import { router } from './router'

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
  ) {
    super(message)
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/console/api${path}`, {
    method,
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as { message?: string; code?: string }
  if (res.status === 401 && path !== '/login') {
    void router.push({ name: 'login', query: { next: router.currentRoute.value.fullPath } })
  }
  if (!res.ok) throw new ApiError(json.message || `Request failed (${res.status})`, res.status, json.code)
  return json as T
}

export const api = {
  get: <T>(path: string) => call<T>('GET', path),
  post: <T>(path: string, body: unknown = {}) => call<T>('POST', path, body),
  patch: <T>(path: string, body: unknown) => call<T>('PATCH', path, body),
  del: <T>(path: string) => call<T>('DELETE', path),
}

/** The public catalog: asset ids providers map to, and wallet ids. */
export const catalog = () =>
  fetch('/v1/assets').then((r) => r.json()) as Promise<{
    assets: { id: string; coin: string; coin_name: string; network: string; network_name: string; chain_id: number | null; contract: string | null; decimals: number }[]
    wallets: { id: string; name: string; kind: 'wallet' | 'exchange' }[]
  }>

export interface Me {
  user: { email: string }
  tenant: { id: string; slug: string; name: string; checkout_host: string | null }
}

export interface Theme {
  brandName: string
  logoUrl: string | null
  logoDarkUrl: string | null
  faviconUrl: string | null
  primary: string
  onPrimary: string
  background: string
  surface: string
  text: string
  muted: string
  radius: number
  mode: 'light' | 'dark' | 'auto'
  headingFont: FontChoice
  bodyFont: FontChoice
  headingFontUrl: string | null
  bodyFontUrl: string | null
  headingFontName: string | null
  bodyFontName: string | null
  supportUrl: string | null
  showPoweredBy: boolean
}

export interface CheckoutSettings {
  assets: string[]
  wallets: string[]
  expires_minutes: number
  deposit_url: string | null
  status_url: string | null
}

export interface SessionRow {
  id: string
  status: string
  amount: string
  currency: string
  order_id: string | null
  asset: string | null
  crypto_amount: string | null
  created_at: string
}

export interface SessionDetail {
  id: string
  url: string
  status: string
  amount: string
  currency: string
  order_id: string | null
  description: string | null
  assets: string[]
  deposit_url: string | null
  status_url: string | null
  metadata: Record<string, string> | null
  deposit: { asset: string; address: string; amount: string; memo: string | null; reference: string | null; expires_at: string | null; wallet: string | null } | null
  paid_amount: string | null
  /** Underpaid only: what the buyer still has to send. */
  amount_due: string | null
  tx_hash: string | null
  expires_at: string
  created_at: string
  updated_at: string
}

/** One step of a payment's timeline (GET /sessions/:id/timeline). */
export interface TimelineItem {
  id: string
  at: string
  actor: 'provider' | 'flowfront' | 'buyer' | 'console'
  type: 'session.created' | 'checkout.opened' | 'deposit.chosen' | 'deposit.requested' | 'deposit.set' | 'event.received' | 'status.requested' | 'status.changed'
  title: string
  http: { method: string; url: string; status: number | null; ms: number | null; signature?: string } | null
  request: unknown
  response: unknown
  note: string | null
  ok: boolean
  from: string | null
  to: string | null
}

export interface ApiKey {
  id: string
  prefix: string
  label: string | null
  last_used_at: string | null
  created_at: string
}

export interface Domain {
  host: string
  kind: 'platform' | 'custom'
  status: 'pending' | 'active'
  dns: { type: string; name: string; value: string }[]
}

export const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

export function fmtMoney(amount: string, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(amount))
  } catch {
    return `${amount} ${currency}`
  }
}

export function fmtRelative(s: string | null) {
  if (!s) return '—'
  const diff = (Date.now() - new Date(s).getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`
  return fmtDate(s)
}
